import { faInfoCircle } from '@fortawesome/pro-light-svg-icons'
import { Box, Flex } from '@pinchenterprisesnpm/friday-ui'
import { InfoBanner } from '@src/features/shift-post-v2/components/info-banner'
import { ReorderRosters } from '@src/features/shift-post-v2/state/shift-post-v2.actions'
import { selectRosters } from '@src/features/shift-post-v2/state/shift-post-v2.selectors'
import { ACCEPTANCE_CRITERIA_OPTION } from '@src/features/shift-post-v2/steps/acceptance-criteria-step'
import { useAccordionsLogic } from '@src/features/shift-post-v2/steps/invite-upshifters-step/components/invite-upshifters-section/hooks/useAccordionsLogic'
import { REORDERING_AVAILABLE } from '@src/features/shift-post-v2/steps/invite-upshifters-step/components/invite-upshifters-section/invite-upshifters-section'
import { RosterSection } from '@src/features/shift-post-v2/steps/invite-upshifters-step/components/roster-section'
import { connect, FormikProps } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo } from 'react'
import { getSelectedAcceptanceCriteriaOption } from '@src/features/shift-post-v2/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { useInvitesHistory } from '@src/features/shift-post-v2/hooks/useInvitesHistory'
import { ROSTER_INVITE_STATUS } from '@network/models/roster.model'
import { IInviteUpshiftersFormValues } from '../../invite-upshifters-section.types'

interface IInviteUpshiftersContentProps {
  formik?: FormikProps<IInviteUpshiftersFormValues>
}

export const InviteUpshiftersContent = connect<IInviteUpshiftersContentProps>(
  ({ formik }) => {
    const { values } = formik
    const dispatch = useDispatch()

    const selectedAcceptanceCriteriaOption = useMemo(() => {
      return getSelectedAcceptanceCriteriaOption(
        values.auto_accept_by_rosters,
        values.auto_accept,
      )
    }, [values.auto_accept_by_rosters, values.auto_accept])

    // Acceptance criteria selected rosters
    const acSelectedRosters = values.auto_accept_rosters

    // All rosters
    const allRosters = useSelector(selectRosters)

    const rosters =
      selectedAcceptanceCriteriaOption ===
      ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS
        ? acSelectedRosters
        : allRosters

    // Keep selectedRostersList for reordering logic
    const selectedRostersList = useMemo(
      () => rosters.filter(roster => values.roster_ids?.includes(roster.id)),
      [rosters, values.roster_ids],
    )

    const isRosterPositionValid = useCallback(
      (index: number) => {
        // Only allow positions within the selected rosters range
        return index >= 0 && index < (values.roster_ids?.length ?? 0)
      },
      [values.roster_ids],
    )

    const { expandedRosterIds, handleExpandRosterToggle } = useAccordionsLogic()

    const { pendingRosterIds, finishedRosterIds } = useInvitesHistory()

    return (
      <Box>
        {REORDERING_AVAILABLE && (
          <InfoBanner
            description="Use the reorder buttons to set the priority of which rosters will be invited. Rosters at the top will be invited first."
            icon={faInfoCircle}
          />
        )}

        <AnimatePresence>
          {rosters.map(roster => {
            const isExpanded = expandedRosterIds.includes(roster.id)
            const isPending = pendingRosterIds.has(roster.id)
            const isFinished = finishedRosterIds.has(roster.id)

            const getInviteStatus = () => {
              if (isPending) {
                return ROSTER_INVITE_STATUS.PENDING
              }
              if (isFinished) {
                return ROSTER_INVITE_STATUS.FINISHED
              }
              return undefined
            }

            // Find position within selected rosters
            const selectedRosterIndex = selectedRostersList.findIndex(
              selectedRoster => selectedRoster.id === roster.id,
            )

            const selectedOneBasedIndex = selectedRosterIndex + 1

            const handleMoveRosterPosition = (newIndex: number) => {
              // Only allow reordering if this roster is selected
              if (!values.roster_ids?.includes(roster.id)) {
                return
              }

              // Validate the new index is within selected rosters range
              if (newIndex < 0 || newIndex >= selectedRostersList.length) {
                return
              }

              // Find the current position in the full roster list
              const currentPositionInFullList = allRosters.findIndex(
                r => r.id === roster.id,
              )

              // Find the target position in the full roster list
              const targetRosterId = selectedRostersList[newIndex]?.id
              const targetPositionInFullList = allRosters.findIndex(
                r => r.id === targetRosterId,
              )

              // If we can't find the target roster, use the current position as fallback
              const newPositionInFullList =
                targetPositionInFullList !== -1
                  ? targetPositionInFullList
                  : currentPositionInFullList

              dispatch(
                ReorderRosters({
                  oldIndex: currentPositionInFullList,
                  newIndex: newPositionInFullList,
                }),
              )
            }

            return (
              <motion.div
                key={roster.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{
                  layout: { duration: 0.3, ease: 'easeInOut' },
                  opacity: { duration: 0.2 },
                  y: { duration: 0.2 },
                }}
              >
                <Flex alignItems="center">
                  <Box style={{ flex: 1 }}>
                    <RosterSection
                      roster={roster}
                      isExpanded={isExpanded}
                      onToggle={handleExpandRosterToggle}
                      handleMoveRosterPosition={handleMoveRosterPosition}
                      oneBasedIndex={selectedOneBasedIndex}
                      isRosterPositionValid={isRosterPositionValid}
                      isUnavailable={!(roster.is_available_for_invite ?? true)}
                      hideReordering={(values.roster_ids ?? []).length === 0}
                      invite_status={getInviteStatus()}
                    />
                  </Box>
                </Flex>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </Box>
    )
  },
)
